// Loading and error states, in the same calm voice as the rest (docs/brand.md, Voice). An empty
// state is d's EmptyNote.

import type { ReactNode } from 'react'
import { erasing, erasingSubject, isApiError } from '../api/client'
import { useI18n } from '../i18n'
import { EmptyNote } from './d'

export function Loading({ label }: { label?: string }) {
  const { t } = useI18n()
  return (
    <p
      role="status"
      data-loading=""
      className="mono dim"
      style={{ fontSize: 13, padding: '14px 18px' }}
    >
      {label ?? t('state.loading')}
    </p>
  )
}

/** An API failure, worded by what the user can do about it. */
export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const { t } = useI18n()
  // Being deleted (APS-406, a 410): nothing went wrong and nothing to retry, so it reads as a
  // state of the page, not as an error.
  if (erasing(error)) return <ErasingNote subject={erasingSubject(error)} />
  const message = isApiError(error, 403)
    ? t('state.forbidden')
    : isApiError(error, 404)
      ? t('state.notFound')
      : isApiError(error, 401)
        ? t('state.signedOut')
        : t('state.failed')
  return (
    <div role="alert" className="notice error stack">
      <p>{message}</p>
      {retry && !isApiError(error, 403) && !isApiError(error, 404) ? (
        <button
          type="button"
          className="btn small"
          onClick={retry}
          style={{ justifySelf: 'start' }}
        >
          {t('state.retry')}
        </button>
      ) : null}
    </div>
  )
}

/**
 * The project, or its organization, is being deleted (APS-406): the console's EmptyNote, under
 * the page's own head; the same for one panel and for the shell's whole page.
 */
export function ErasingNote({
  subject,
  children,
}: {
  subject: 'organization' | 'project'
  /**
   * What belongs to the note, right under it (a project's deletion: its date and Cancel,
   * APS-401): one block with it, so a page's grid gap never comes between them.
   */
  children?: ReactNode
}) {
  const { t } = useI18n()
  const note = (
    <div role="status">
      <EmptyNote
        title={t(subject === 'organization' ? 'state.erasingOrg' : 'state.erasing')}
        tight={!!children}
      >
        {t('state.erasingDetail')}
      </EmptyNote>
    </div>
  )
  return children ? (
    <div className="erasing-note">
      {note}
      {children}
    </div>
  ) : (
    note
  )
}
